"use client";

import { useEffect, useState } from "react";
import { useSidebar } from "../ui/sidebar";
import { useStatisticsContext } from "./statistics_context";
import { get } from "../../lib/api";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Download } from "lucide-react";
import { CartesianGrid, Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../../components/ui/chart";
import { Select } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import { useIsMobile } from "../../hooks/use-mobile";

const options = [
  "Total Unique Users who entered the game",
  "Total Unique Users who played the daily challenge",
  "Percentage of Users who completed the daily challenge",
  "Percentage of Users who gave up on the daily challenge",
  "Total Time for entire game",
  "Total Gameplays",
  "Total Rewarded Retries in Daily Challenge",
  "Total Rewarded Retries in Freeplay",
  "Total Freeplay Gameplays",
  "Total Midroll Ads",
  "Total Ads",
  "Average Time per gameplay in minutes",
];

const rangeOptions = [
  { label: "1 month", value: "1m" },
  { label: "3 months", value: "3m" },
  { label: "6 months", value: "6m" },
  { label: "All time", value: "all" },
  { label: "Custom", value: "custom" },
];

const DownloadCSVButton = ({ data, fileName, isMobile }) => {
  const handleDownload = () => {
    if (!data || !data.date || !Array.isArray(data.date)) return;

    const keys = Object.keys(data).filter((key) => Array.isArray(data[key]));
    const rowCount = data.date.length;
    const rows = [];

    for (let i = 0; i < rowCount; i++) {
      const row = {};
      keys.forEach((key) => {
        row[key] = data[key]?.[i] ?? "";
      });
      rows.push(row);
    }

    const headers = keys.join(",");
    const csvData = rows.map((row) => keys.map((key) => row[key]).join(",")).join("\n");

    const csv = `${headers}\n${csvData}`;

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Button onClick={handleDownload} variant="outline" size={isMobile ? "sm" : "sm"} className="flex items-center gap-2">
      <Download className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
      <span className={isMobile ? "text-xs" : ""}>Download CSV</span>
    </Button>
  );
};

const GraphWithOptions = ({ game_id }) => {
  const isMobile = useIsMobile();

  const [selectedStatistics, setSelectedStatistics] = useState(["Total Unique Users who entered the game"]);
  const [selectedOptions, setSelectedOptions] = useState({
    "Total Unique Users who entered the game": true,
    "Total Unique Users who played the daily challenge": false,
    "Percentage of Users who completed the daily challenge": false,
    "Percentage of Users who gave up on the daily challenge": false,
    "Total Time for entire game": false,
    "Total Gameplays": false,
    "Total Rewarded Retries in Daily Challenge": false,
    "Total Rewarded Retries in Freeplay": false,
    "Total Freeplay Gameplays": false,
    "Total Midroll Ads": false,
    "Total Ads": false,
    "Average Time per gameplay in minutes": false,
  });
  const [chartData, setChartData] = useState([]);
  const [isGeneralDataLoaded, setIsGeneralDataLoaded] = useState(false);
  const [chartConfig, setChartConfig] = useState({});

  const { brand, dates } = useSidebar();
  const { generalStatistics, setGeneralStatistics, setGeneralStatsLoading } = useStatisticsContext();

  // Date range state
  const [rangeOption, setRangeOption] = useState("3m");
  const [startDate, setStartDate] = useState(() => {
    // Default to 3 months ago
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1); // yesterday
    return d.toISOString().split("T")[0];
  });

  // Update date fields when dropdown changes
  useEffect(() => {
    if (rangeOption === "custom") return;
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    let newStart;
    if (rangeOption === "1m") {
      newStart = new Date(yesterday);
      newStart.setMonth(newStart.getMonth() - 1);
    } else if (rangeOption === "3m") {
      newStart = new Date(yesterday);
      newStart.setMonth(newStart.getMonth() - 3);
    } else if (rangeOption === "6m") {
      newStart = new Date(yesterday);
      newStart.setMonth(newStart.getMonth() - 6);
    } else if (rangeOption === "all") {
      newStart = new Date(dates[0]);
    }
    setStartDate(newStart.toISOString().split("T")[0]);
    setEndDate(yesterday.toISOString().split("T")[0]);
  }, [rangeOption, dates]);

  // If user changes date fields, set dropdown to custom
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setRangeOption("custom");
  };
  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setRangeOption("custom");
  };

  useEffect(() => {
    const fetchGeneralData = async () => {
      setGeneralStatsLoading(true);
      const data = await get(`/statistics?method=getGeneralStatisticsNew&gameid=${game_id}&date=${startDate}_${endDate}`);
      setGeneralStatistics(data);
      setIsGeneralDataLoaded(true);
      setGeneralStatsLoading(false);
    };
    fetchGeneralData();
  }, [game_id, brand, startDate, endDate, setGeneralStatistics, setGeneralStatsLoading]);

  useEffect(() => {
    if (game_id === null || !isGeneralDataLoaded || !generalStatistics) return;

    // Create chart config with colors for each selected statistic
    const newConfig = {};
    selectedStatistics.forEach((stat, index) => {
      // Generate a unique color for each statistic
      // Using hsl colors with different hues
      const hue = (index * 137) % 360; // Golden ratio to distribute colors
      newConfig[stat] = {
        label: stat,
        color: `hsl(${hue}, 70%, 50%)`,
      };
    });

    // We need to convert from separate arrays to an array of objects
    const transformedData = [];

    if (generalStatistics.date) {
      for (let i = 0; i < generalStatistics.date.length; i++) {
        const dataPoint = {
          date: generalStatistics.date[i],
        };

        // Add selected statistics to each data point
        selectedStatistics.forEach((stat) => {
          if (generalStatistics[stat] && generalStatistics[stat][i] !== undefined) {
            dataPoint[stat] = generalStatistics[stat][i];
          }
        });

        transformedData.push(dataPoint);
      }
    }

    setChartData(transformedData);
    setChartConfig(newConfig);
  }, [selectedStatistics, isGeneralDataLoaded, generalStatistics, game_id]);

  const handleCheckboxChange = (option) => {
    // Update the selected options
    setSelectedOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));

    // Update the selected statistics
    setSelectedStatistics((prev) => {
      const currentIndex = prev.indexOf(option);
      if (currentIndex > -1) {
        return prev.filter((stat) => stat !== option);
      } else {
        return [...prev, option];
      }
    });
  };

  // Format date for x-axis
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div style={{width: isMobile ? '100%' : '87.5vw'}}>
      <div className={`w-full bg-[#ffffff] text-[#232833] rounded-lg shadow-md dark:bg-[#232833] dark:text-[#f3f4f6] ${isMobile ? 'p-2' : 'p-4'}`}>
        {/* Row 1: Graph/chart (full width) */}
        <div className="w-full mb-4">
          <div className={`w-full rounded-lg bg-bg-[#ffffff] text-[#232833] dark:bg-[#2c313c] dark:text-[#f3f4f6] ${isMobile ? 'h-[300px]' : 'h-[400px]'}`}>
            {chartData.length > 0 ? (
              <ChartContainer config={chartConfig} className="h-full w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 30, bottom: 50 }}>
                    {/* Gradient definitions for each selected statistic */}
                    <defs>
                      {selectedStatistics.map((stat, index) => (
                        <linearGradient key={stat} id={`gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={chartConfig[stat]?.color || "#000000"} stopOpacity={0.3} />
                          <stop offset="100%" stopColor={chartConfig[stat]?.color || "#000000"} stopOpacity={0} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={formatDate} 
                      tickMargin={10} 
                      tickLine={true} 
                      axisLine={true}
                      tick={{ fontSize: isMobile ? 10 : 12 }}
                    />
                    {!isMobile && <YAxis tickMargin={10} tickLine={true} axisLine={true} width={30} />}
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          labelFormatter={(value) => {
                            return new Date(value).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            });
                          }}
                        />
                      }
                    />
                    {selectedStatistics.map((stat, index) => (
                      <Area
                        key={stat}
                        type="monotone"
                        dataKey={stat}
                        stroke={chartConfig[stat]?.color || "#000000"}
                        fill={`url(#gradient-${index})`}
                        fillOpacity={1}
                        strokeWidth={2}
                        activeDot={{ r: 6 }}
                        name={stat}
                        style={{ mixBlendMode: 'normal' }}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className={isMobile ? 'text-sm' : ''}>No data available to display</p>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Range controls and Download CSV button */}
        <div className={`flex flex-col ${isMobile ? 'gap-2' : 'md:flex-row md:items-center md:justify-between gap-4'} mt-4`}>
          <div className={`flex ${isMobile ? 'flex-col' : 'flex-wrap'} gap-2 ${isMobile ? 'h-auto' : 'h-[70px]'}`}>
            <div>
              <label className={`block font-medium mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>Range</label>
              <select
                className={`border rounded px-2 py-1 bg-[#ffffff] shadow-md text-[#232833] dark:bg-[#2c313c] dark:text-[#f3f4f6] ${isMobile ? 'w-full text-xs' : ''}`}
                value={rangeOption}
                onChange={e => setRangeOption(e.target.value)}
              >
                {rangeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block font-medium mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>Start Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant={"outline"} 
                    className={cn(
                      `${isMobile ? 'w-full text-xs' : 'w-[180px]'} justify-start text-left font-normal`, 
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className={`mr-2 ${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                    {startDate ? format(new Date(startDate), "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className={`w-auto p-0 ${isMobile ? 'w-[280px]' : ''}`}>
                  <Calendar
                    mode="single"
                    selected={startDate ? new Date(startDate) : undefined}
                    onSelect={date => {
                      if (date) setStartDate(format(date, "yyyy-MM-dd"));
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className={`block font-medium mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>End Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant={"outline"} 
                    className={cn(
                      `${isMobile ? 'w-full text-xs' : 'w-[180px]'} justify-start text-left font-normal`, 
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className={`mr-2 ${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                    {endDate ? format(new Date(endDate), "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className={`w-auto p-0 ${isMobile ? 'w-[280px]' : ''}`}>
                  <Calendar
                    mode="single"
                    selected={endDate ? new Date(endDate) : undefined}
                    onSelect={date => {
                      if (date) setEndDate(format(date, "yyyy-MM-dd"));
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className={`flex ${isMobile ? 'justify-center' : 'justify-end md:justify-end'}`}>
            <DownloadCSVButton data={generalStatistics} fileName={`General Stats for Game ID ${game_id}`} isMobile={isMobile} />
          </div>
        </div>

        {/* Row 3: Checkboxes */}
        <div className={`w-full flex-1 grid ${isMobile ? 'grid-cols-1 gap-1' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2'}`}>
          {options.map((option) => (
            <div key={option} className="flex items-start space-x-2">
              <div className="rounded bg-[#ffffff] dark:bg-[#232833] p-1 flex items-center">
                <Checkbox
                  className="shadow-none data-[state=checked]:bg-purple-700 dark:data-[state=checked]:bg-purple-700 text-[#232833] dark:text-[#f3f4f6]"
                  id={option}
                  checked={selectedOptions[option]}
                  onCheckedChange={() => handleCheckboxChange(option)}
                />
              </div>
              <Label 
                htmlFor={option} 
                className={`font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer ${isMobile ? 'text-xs' : 'text-sm'}`}
              >
                {option}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GraphWithOptions;
